import React, { useState, useCallback, useEffect } from 'react';
import { Modal, ModalTitle, ModalContent, ModalActions, TextInput, Button, Alert, Label } from '@c2fo/liquidity';
import { EyeIcon, EyeCrossedIcon } from '@c2fo/liquidity/icons';

interface ResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
  onReset: (password: string) => Promise<void>;
  userEmail: string;
  isLoading?: boolean;
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  open,
  onClose,
  onReset,
  userEmail,
  isLoading = false,
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | undefined>();
  const [showPassword, setShowPassword] = useState(false);

  // Reset form state when modal closes
  useEffect(() => {
    if (!open) {
      setPassword('');
      setError(null);
      setValidationError(undefined);
      setShowPassword(false);
    }
  }, [open]);

  const validatePassword = useCallback((value: string): string | undefined => {
    if (!value) {
      return 'Password is required';
    }

    if (value.length < 12) {
      return 'Password must be at least 12 characters long';
    }

    return undefined;
  }, []);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setValidationError(validatePassword(value));
  };

  const handleSubmit = async () => {
    const passwordError = validatePassword(password);

    if (passwordError) {
      setValidationError(passwordError);
      return;
    }

    setError(null);
    try {
      await onReset(password);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    }
  };

  const handleClose = () => {
    setPassword('');
    setError(null);
    setValidationError(undefined);
    setShowPassword(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="sm">
      <ModalTitle>Reset Password?</ModalTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <ModalContent>
          <div className="space-y-4">
            <p className="text-gray-700">
              Setting a temporary password for: <strong>{userEmail}</strong>
            </p>
            <p className="text-gray-700 mb-4">
              User will be prompted to reset their password the next time they log in.
            </p>

            {error && (
              <div className="mb-4">
                <Alert variant="filled" type="error" full description={error} />
              </div>
            )}

            <div>
              <Label htmlFor="newPassword" className="block mb-2 font-semibold">
                New Password:
              </Label>
              <div className="relative">
                <TextInput
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder=""
                  className="w-full pr-10"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeCrossedIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 cursor-pointer" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 cursor-pointer" />
                  )}
                </button>
              </div>
              {validationError && <p className="text-sm text-red-600 mt-1">{validationError}</p>}
            </div>
          </div>
        </ModalContent>
        <ModalActions>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            disabled={isLoading || !password || !!validationError}
          >
            Reset
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};
